import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Loader2, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

// Format YYYY-MM-DD → "March 14, 1988"
function fmtDob(iso) {
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}
// JS Date → YYYY-MM-DD without timezone slip
function toIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// PUB-03 — Application form (rendered inside StandaloneShell with APPLY_ACCENT)
export default function ApplicationPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", dob: "", ssnLast4: "",
    address: "", city: "", state: "", zip: "",
    email: "", phone: "", preferredContact: "email",
    sourceDeclared: false, sourceDetail: "",
    monthlyVolume: "",
    terms: false,
  });
  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        firstName:        form.firstName.trim(),
        lastName:         form.lastName.trim(),
        email:            form.email.trim(),
        phone:            form.phone.trim(),
        dateOfBirth:      form.dob,
        ssnLast4:         form.ssnLast4,
        address: {
          street: form.address.trim(),
          city:   form.city.trim(),
          state:  form.state,
          zip:    form.zip.trim(),
        },
        referralSource:   form.sourceDetail || "",
        monthlyVolumeUSD: form.monthlyVolume ? Number(form.monthlyVolume) : null,
        sourceOfCards:    form.sourceDetail || null,
        termsAccepted:    !!form.terms,
      };
      const res = await fetch(`${API}/applications`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error("Couldn't submit application", { description: err.detail || `HTTP ${res.status}` });
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Network error", { description: String(err) });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center">
        <div className="mx-auto size-14 rounded-full bg-brand-50 text-brand-700 grid place-items-center mb-7 ring-4 ring-brand-100/40">
          <CheckCircle2 className="size-7" strokeWidth={2.2} />
        </div>
        <div className="eyebrow text-brand-700 mb-2">Application received</div>
        <h1 className="font-brand text-display text-ink leading-tight font-bold tracking-[-0.02em]">
          Welcome, {form.firstName || "applicant"}.
        </h1>
        <p className="mt-5 text-body text-ink-muted leading-relaxed">
          We&rsquo;ve queued your application for review. Most decisions land within two business days.
          You&rsquo;ll get an email at <span className="font-semibold text-ink">{form.email || "your address"}</span> with next steps.
        </p>
        <div className="mt-9 flex items-center justify-center gap-3">
          <Link to="/login">
            <Button variant="outline" className="h-12 px-6 rounded-full border-2 border-line text-ink">Sign in</Button>
          </Link>
          <Link to="/" className="group relative inline-flex items-center gap-2 h-12 pl-6 pr-2 rounded-full bg-brand-700 hover:bg-brand-800 text-white font-semibold text-body-sm transition-all">
            <span>Back to home</span>
            <span className="size-8 rounded-full bg-white/15 grid place-items-center transition-transform group-hover:rotate-45">
              <ArrowRight className="size-3.5" strokeWidth={2.4} />
            </span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-7">
      <div>
        <div className="eyebrow mb-2 text-brand-700">Apply to join</div>
        <h1 className="font-brand text-[32px] leading-[1.1] tracking-[-0.02em] text-ink font-bold">
          Tell us about you.
        </h1>
        <p className="mt-2 text-body-sm text-ink-muted leading-relaxed">
          Four short sections &middot; ~3 minutes &middot; decision within two business days.
        </p>
      </div>

      <form onSubmit={onSubmit} data-testid="application-form" className="space-y-8">
          <Section number="01" title="Personal" subtitle="As shown on your government ID.">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="First name" required><Input value={form.firstName} onChange={handle("firstName")} required data-testid="apply-first-name" className="h-12 bg-surface" /></Field>
              <Field label="Last name" required><Input value={form.lastName} onChange={handle("lastName")} required data-testid="apply-last-name" className="h-12 bg-surface" /></Field>
              <Field label="Date of birth" required>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="apply-dob"
                            className={cn(
                              "h-12 w-full rounded-md border bg-surface px-3 text-left text-body-sm flex items-center justify-between transition-colors focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-700",
                              form.dob ? "text-ink border-line" : "text-ink-hint border-line"
                            )}>
                      <span className="tabular-nums">
                        {form.dob ? fmtDob(form.dob) : "Select date"}
                      </span>
                      <CalendarIcon className="size-4 text-ink-hint shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-auto" align="start">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown-buttons"
                      fromYear={1925}
                      toYear={new Date().getFullYear() - 18}
                      defaultMonth={form.dob ? new Date(form.dob) : new Date(2000, 0, 1)}
                      selected={form.dob ? new Date(form.dob) : undefined}
                      disabled={(d) => d > new Date()}
                      onSelect={(d) => setForm((f) => ({ ...f, dob: d ? toIsoDate(d) : "" }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </Field>
              <Field label="SSN last 4" required hint="Used for KYC sanctions matching only.">
                <Input maxLength={4} pattern="\d{4}" value={form.ssnLast4} onChange={handle("ssnLast4")} required placeholder="••••" data-testid="apply-ssn" className="h-12 bg-surface tabular-nums" />
              </Field>
            </div>
          </Section>

          <Section number="02" title="Address" subtitle="US residents only at this time.">
            <div className="grid sm:grid-cols-6 gap-4">
              <Field label="Street" required className="sm:col-span-6"><Input value={form.address} onChange={handle("address")} required data-testid="apply-address" className="h-12 bg-surface" /></Field>
              <Field label="City" required className="sm:col-span-3"><Input value={form.city} onChange={handle("city")} required data-testid="apply-city" className="h-12 bg-surface" /></Field>
              <Field label="State" required className="sm:col-span-2">
                <Select value={form.state} onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}>
                  <SelectTrigger data-testid="apply-state" className="h-12 bg-surface"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="max-h-[260px]">
                    {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="ZIP" required className="sm:col-span-1"><Input maxLength={5} value={form.zip} onChange={handle("zip")} required data-testid="apply-zip" className="h-12 bg-surface tabular-nums" /></Field>
            </div>
          </Section>

          <Section number="03" title="Contact" subtitle="We&rsquo;ll only email you about reservations and payments.">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email" required><Input type="email" value={form.email} onChange={handle("email")} required data-testid="apply-email" className="h-12 bg-surface" /></Field>
              <Field label="Phone" required><Input type="tel" value={form.phone} onChange={handle("phone")} required placeholder="(555) 555-5555" data-testid="apply-phone" className="h-12 bg-surface" /></Field>
              <Field label="Preferred contact" className="sm:col-span-2">
                <div className="flex gap-2">
                  {["email", "phone"].map((m) => (
                    <button key={m} type="button" onClick={() => setForm((f) => ({ ...f, preferredContact: m }))}
                            className={`px-4 py-2.5 rounded-md border text-body-sm font-medium capitalize transition-all ${form.preferredContact === m ? "bg-brand-50 border-brand-700 text-brand-800" : "border-line bg-surface text-ink-muted hover:border-ink"}`}>
                      {m}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
          </Section>

          <Section number="04" title="Source of cards" subtitle="We reject anonymous-trade, reseller-resale, and bulk peer-to-peer sources.">
            <div className="grid sm:grid-cols-5 gap-4">
              <Field label="Where do your gift cards come from?" required hint="Be specific. e.g., 'Performance bonuses paid in retailer cards by employer'." className="sm:col-span-3">
                <Textarea rows={4} value={form.sourceDetail} onChange={handle("sourceDetail")} required maxLength={400}
                          data-testid="apply-source"
                          className="bg-surface"
                          placeholder="My employer pays quarterly bonuses in retailer gift cards…" />
              </Field>
              <Field label="Monthly volume" required className="sm:col-span-2">
                <Select value={form.monthlyVolume} onValueChange={(v) => setForm((f) => ({ ...f, monthlyVolume: v }))}>
                  <SelectTrigger className="h-12 bg-surface" data-testid="apply-volume"><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lt2000">Under $2,000 / month</SelectItem>
                    <SelectItem value="2-5">$2,000 – $5,000 / month</SelectItem>
                    <SelectItem value="5-10">$5,000 – $10,000 / month</SelectItem>
                    <SelectItem value="gt10">Over $10,000 / month</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          <div className="border-t border-line pt-5 space-y-4">
            <label className="flex items-start gap-3 text-body-sm text-ink-muted leading-relaxed">
              <Checkbox checked={form.terms} onCheckedChange={(v) => setForm((f) => ({ ...f, terms: !!v }))} className="mt-0.5" required data-testid="apply-terms" />
              <span>
                I confirm the source of my cards is legal and verifiable, and I agree to the{" "}
                <Link to="/faq" className="text-brand-700 underline">Terms</Link> and{" "}
                <Link to="/faq" className="text-brand-700 underline">Privacy Policy</Link>.
              </span>
            </label>

            <div className="flex items-center justify-between gap-3">
              <Link to="/" className="text-body-sm text-ink-muted hover:text-ink">Cancel</Link>
              <button type="submit" disabled={!form.terms || submitting} data-testid="apply-submit-btn"
                      className="group relative inline-flex items-center gap-3 h-12 pl-7 pr-3 rounded-full bg-brand-700 hover:bg-brand-800 disabled:bg-line disabled:text-ink-hint disabled:cursor-not-allowed text-white font-brand font-bold text-body-sm shadow-[0_8px_24px_rgba(15,118,110,0.32)] hover:shadow-[0_12px_32px_rgba(15,118,110,0.42)] hover:-translate-y-0.5 transition-all duration-300 disabled:shadow-none disabled:translate-y-0">
                <span>{submitting ? "Submitting…" : "Submit application"}</span>
                <span className="size-8 rounded-full bg-white/15 grid place-items-center transition-transform group-hover:rotate-45 group-hover:bg-white/25">
                  {submitting
                    ? <Loader2 className="size-3.5 animate-spin" strokeWidth={2.4} />
                    : <ArrowRight className="size-3.5" strokeWidth={2.4} />}
                </span>
              </button>
            </div>
          </div>
        </form>

        <div className="text-center text-caption text-ink-muted">
          Already a member? <Link to="/login" className="text-brand-700 font-semibold hover:text-brand-800">Sign in</Link>
        </div>
    </div>
  );
}

function Section({ number, title, subtitle, children }) {
  return (
    <section>
      <div className="flex items-baseline gap-3 mb-4">
        <span className="font-mono text-caption text-brand-700 tabular-nums">{number}</span>
        <div>
          <h2 className="font-brand text-h3 text-ink font-semibold">{title}</h2>
          {subtitle && <p className="mt-0.5 text-caption text-ink-subtle leading-snug max-w-lg">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function Field({ label, required, hint, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-eyebrow uppercase text-ink-muted mb-1.5 block">
        {label} {required && <span className="text-brand-700 normal-case">*</span>}
      </Label>
      {children}
      {hint && <p className="mt-1.5 text-caption text-ink-hint">{hint}</p>}
    </div>
  );
}
